/* eslint-disable no-console */
import { isDevelopment } from './EnvManager';

const green = '\x1b[32m';
const yellow = '\x1b[93m';
const blue = '\x1b[34m';
const red = '\x1b[31m';
const gray = '\x1b[90m';
const cyan = '\x1b[36m';

enum ELogger {
  DEFAULT = '',
  LOG = gray,
  DEBUG = blue,
  INFO = green,
  WARNING = yellow,
  ERROR = red,
}

class Logger {
  private colorReset = '\x1b[0m';
  private sourceColor = '\x1b[35m';
  private timestampColor = cyan;

  private getCallerInfo(): string {
    try {
      const stack = new Error().stack;
      if (!stack) return '';

      const lines = stack.split('\n');

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();

        if (
          line.includes('LoggerManager') ||
          line.includes('Logger.') ||
          line.includes('logMessage') ||
          line.includes('formatMessage') ||
          line.includes('getCallerInfo')
        ) {
          continue;
        }

        const patterns = [
          // Vite/browser: "at functionName (http://localhost:5173/src/services/datadog-rum.ts:10:5)"
          /at\s+.*?\s+\(.*?\/([^/]+\.(ts|tsx|js|jsx)):(\d+):\d+\)/,

          // Chrome: "at funcName (file.ts:123:45)"
          /at\s+.*?\s+\(([^/\\]+\.(ts|tsx|js|jsx)):(\d+):\d+\)/,

          // Chrome sem função: "at file.ts:123:45"
          /at\s+([^/\\:]+\.(ts|tsx|js|jsx)):(\d+):\d+/,

          // Firefox: "funcName@file.ts:123:45"
          /@([^/\\:]+\.(ts|tsx|js|jsx)):(\d+):\d+/,

          // Node.js
          /\s+at\s+.*?\s+\(([^)]+\.(ts|tsx|js|jsx)):(\d+):\d+\)/,

          // Webpack
          /webpack:\/\/\/\.\/([^:]+\.(ts|tsx|js|jsx)):(\d+):\d+/,

          // Pattern mais geral para capturar qualquer arquivo
          /([^/\\:\s]+\.(ts|tsx|js|jsx)):(\d+)/,
        ];

        for (const pattern of patterns) {
          const match = line.match(pattern);
          if (match) {
            const fullPath = match[1];
            const lineNumber = match[match.length - 1];
            const cleanFileName = fullPath.split('/').pop() || fullPath;

            return `${this.sourceColor}[${cleanFileName}:${lineNumber}]${this.colorReset} `;
          }
        }
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  private getTimestamp(): string {
    return new Date().toISOString().replace('T', ' ').slice(0, 19);
  }

  private formatMessage(type: ELogger, message: string): string {
    const timestamp = isDevelopment ? `${this.timestampColor}[${this.getTimestamp()}]${this.colorReset}` : '';
    const source = isDevelopment ? this.getCallerInfo() : '';
    return `${timestamp} ${source}${type}${message}${this.colorReset}`;
  }

  private processArguments(args: Array<unknown>): string {
    return args
      .map((arg) => {
        if (arg === null) return 'null';
        if (arg === undefined) return 'undefined';
        if (typeof arg === 'string') return arg;
        if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);

        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  private logMessage(type: ELogger, ...args: Array<unknown>): void {
    const message = this.processArguments(args);
    const formattedMessage = this.formatMessage(type, message);

    console.log(formattedMessage);
  }

  public log(...args: Array<unknown>): void {
    this.logMessage(ELogger.LOG, ...args);
  }

  public debug(...args: Array<unknown>): void {
    if (isDevelopment) {
      this.logMessage(ELogger.DEBUG, ...args);
    }
  }

  public info(...args: Array<unknown>): void {
    if (isDevelopment) {
      this.logMessage(ELogger.INFO, ...args);
    } else {
      this.logMessage(ELogger.DEFAULT, ...args);
    }
  }

  public warn(...args: Array<unknown>): void {
    this.logMessage(ELogger.WARNING, ...args);
  }

  public error(...args: Array<unknown>): void {
    this.logMessage(ELogger.ERROR, ...args);
  }
}

const logger = new Logger();

declare global {
  interface Window {
    logger: Logger;
  }
  var logger: Logger;
}

if (typeof window !== 'undefined') {
  window.logger = logger;
}

if (typeof global !== 'undefined') {
  global.logger = logger;
}

export { logger };
